import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {format as formatDate} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {SPLIT_BILLS_QUERY_PARAMS} from '@/app/main/split-bills/constants';
import {IRecordGroupExpenseRequest} from '@/dataSource/src/api/groups/entities';

import {IGroupMember} from '../../models/interfaces';
import {createSplitExpensePrompt} from '../../utils/createSplitExpensePrompt';

import {EAddExpenseSplitMode} from './constants';

interface UseGroupAddExpenseFormParams {
  groupName: string;
  inviterName: string;
  members: IGroupMember[];
  currentUserId?: string;
  onSubmit: (payload: IRecordGroupExpenseRequest) => Promise<void>;
}

const DEFAULT_AMOUNT = '0';
const DEFAULT_AMOUNT_LABEL = '$0.00';

const parseAmountCents = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return Number(digits || DEFAULT_AMOUNT);
};

const splitAmountCents = (totalCents: number, count: number, index: number) => {
  if (count <= 0) return 0;

  const base = Math.floor(totalCents / count);
  const remainder = totalCents % count;

  return base + (index < remainder ? 1 : 0);
};

const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

/**
 * State holder (view-model hook) for `GroupAddExpenseForm`. Owns all
 * local form state and handlers so the component can stay purely
 * presentational. The submission payload contract is preserved.
 */
export function useGroupAddExpenseForm({
  groupName,
  inviterName,
  members,
  currentUserId,
  onSubmit,
}: UseGroupAddExpenseFormParams) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {t} = useTranslation();

  const [title, setTitle] = useState('');
  const [amountLabel, setAmountLabel] = useState(DEFAULT_AMOUNT_LABEL);
  const [when, setWhen] = useState<Date | null>(() => new Date());
  const [splitMode, setSplitMode] = useState(EAddExpenseSplitMode.EQUAL);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [selectedPayerId, setSelectedPayerId] = useState('');
  const [payerModalOpen, setPayerModalOpen] = useState(false);

  const amountCents = useMemo(
    () => parseAmountCents(amountLabel),
    [amountLabel],
  );

  const amount = useMemo(() => amountCents / 100, [amountCents]);

  useEffect(() => {
    if (!members.length) return;

    setSelectedMemberIds(members.map(member => member.id));

    const currentUser = members.find(member => member.id === currentUserId);

    setSelectedPayerId(currentUser?.id || members[0]?.id || '');
  }, [members, currentUserId]);

  const hasInvalidParticipantAmount = useMemo(() => {
    return selectedMemberIds.some((memberId, index) => {
      const shareCents = splitAmountCents(
        amountCents,
        selectedMemberIds.length,
        index,
      );

      return shareCents <= 0;
    });
  }, [amountCents, selectedMemberIds]);

  const canSubmit = useMemo(() => {
    return (
      title.trim().length > 0 &&
      amountCents > 0 &&
      !!when &&
      selectedMemberIds.length > 0 &&
      !!selectedPayerId &&
      !hasInvalidParticipantAmount
    );
  }, [
    title,
    amountCents,
    when,
    selectedMemberIds,
    selectedPayerId,
    hasInvalidParticipantAmount,
  ]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value),
    [],
  );

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '');
      const cents = Number(digits || '0');

      setAmountLabel(formatCurrency(cents / 100));
    },
    [],
  );

  const handleWhenChange = useCallback((date: Date | null) => {
    setWhen(date);
  }, []);

  const handleToggleMember = useCallback((memberId: string) => {
    setSelectedMemberIds(current =>
      current.includes(memberId)
        ? current.filter(id => id !== memberId)
        : [...current, memberId],
    );
  }, []);

  const getMemberShareLabel = useCallback(
    (memberId: string) => {
      const index = selectedMemberIds.indexOf(memberId);

      if (index < 0) return DEFAULT_AMOUNT_LABEL;

      return formatCurrency(
        splitAmountCents(amountCents, selectedMemberIds.length, index) / 100,
      );
    },
    [amountCents, selectedMemberIds],
  );

  const payload = useMemo<IRecordGroupExpenseRequest>(() => {
    const description = title.trim();

    const participants = selectedMemberIds.map((memberId, index) => ({
      split_user_id: memberId,
      amount:
        splitAmountCents(amountCents, selectedMemberIds.length, index) / 100,
    }));

    return {
      expense_date: when?.toISOString(),
      created_by: currentUserId ?? '',
      description,
      payers: [
        {
          split_user_id: selectedPayerId,
          amount,
        },
      ],
      participants,
      detail: {
        subtotal: amount,
        taxes: 0,
        tip: 0,
        total: amount,
        items: [
          {
            description,
            shared_by_ids: selectedMemberIds,
            subtotal: amount,
            tax: 0,
            tip: 0,
            total: amount,
          },
        ],
      },
    };
  }, [
    amount,
    amountCents,
    currentUserId,
    selectedMemberIds,
    selectedPayerId,
    title,
    when,
  ]);

  const selectedPayerName = useMemo(() => {
    const payer = members.find(member => member.id === selectedPayerId);
    if (payer?.id === currentUserId) {
      return `${payer.name} (${t('groups.add_expense_form.you')})`;
    }
    return payer?.name || '';
  }, [members, selectedPayerId, currentUserId, t]);

  const initialMessage = useMemo(() => {
    return createSplitExpensePrompt({
      title: title.trim(),
      amount,
      date: when ? formatDate(when, 'MMMM d') : undefined,
      payerName: selectedPayerName,
      isCurrentUserPayer: selectedPayerId === currentUserId,
    });
  }, [amount, title, when, selectedPayerId, selectedPayerName, currentUserId]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !when) return;
    await onSubmit(payload);
  }, [canSubmit, onSubmit, payload, when]);

  const handleOnClickSplitWithMo = useCallback(() => {
    searchParams.set(SPLIT_BILLS_QUERY_PARAMS.GROUP_NAME, groupName || '');
    searchParams.set(SPLIT_BILLS_QUERY_PARAMS.INVITER_NAME, inviterName || '');
    navigate(`/split-bills?${searchParams.toString()}`, {
      state: {
        contextPayload: JSON.stringify(payload),
        contextMessage: initialMessage,
      },
    });
  }, [groupName, inviterName, navigate, payload, initialMessage, searchParams]);

  const handleOpenPayerModal = useCallback(() => {
    setPayerModalOpen(true);
  }, []);

  const handleClosePayerModal = useCallback(() => {
    setPayerModalOpen(false);
  }, []);

  const handleConfirmPayer = useCallback((payerId: string) => {
    setSelectedPayerId(payerId);
    setPayerModalOpen(false);
  }, []);

  const handleSetSplitEqual = useCallback(
    () => setSplitMode(EAddExpenseSplitMode.EQUAL),
    [],
  );

  const handleSetSplitCustom = useCallback(
    () => setSplitMode(EAddExpenseSplitMode.CUSTOM),
    [],
  );

  const selectedPayer = useMemo(() => {
    return members.find(member => member.id === selectedPayerId);
  }, [members, selectedPayerId]);

  const isEqualMode = splitMode === EAddExpenseSplitMode.EQUAL;
  const isCustomMode = splitMode === EAddExpenseSplitMode.CUSTOM;

  return {
    // Form values
    title,
    amountLabel,
    when,
    splitMode,
    selectedMemberIds,
    selectedPayerId,
    selectedPayerName,
    selectedPayer,
    payerModalOpen,
    canSubmit,
    isEqualMode,
    isCustomMode,
    // Handlers
    handleTitleChange,
    handleAmountChange,
    handleWhenChange,
    handleToggleMember,
    handleSetSplitEqual,
    handleSetSplitCustom,
    handleOpenPayerModal,
    handleClosePayerModal,
    handleConfirmPayer,
    handleSubmit,
    handleOnClickSplitWithMo,
    getMemberShareLabel,
  };
}
